import React, { useRef, useState } from "react";
import * as d3 from "d3";
import { useD3 } from "./d3";
import SFIconButton from "../button/iconButton";
import _ from "lodash";
import CenterFocusWeakIcon from "@mui/icons-material/CenterFocusWeak";
import getStyled from "../../hooks/getStyled";
const { theme, styled } = getStyled();

const StyledContainer = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100%",
  position: "relative",
  backgroundColor: theme.palette.white.main,
}));

const SFIconButtonStyled = styled(SFIconButton)(({ theme }) => ({
  position: "absolute",
  top: 0,
  right: 0,
  opacity: "0.5",
  "&:hover": {
    opacity: 1,
  },
}));

const treeFunctions = {};

const SFTree = ({
  data,
  getDynamicChildren,
  onDataLoaded,
  hasChildrenKey = "hasChildren",
  childrenCountKey = "childrenCount",
  childLevelName = "child",
  childLevelCountKey = "totalChildren",
  totalChildrenKey = "totalChildren",
  maxChildLevelCountKey = "maxChildLevelCount",
  getChildLevelIcon = () => {},
  childDetail = (id, open, onclose) => {},
  barLength = 200,
  openLevel = -1,
  icons = [],
}) => {
  const containerRef = useRef();
  const CIRCLE_RADIUS = 10;
  const CIRCLE_STROKE_WIDTH = 2;
  const TEXT_MARGIN = 20;

  const [drawer, setDrawerState] = useState({ open: false });

  const dx = 30;
  const dy = 370;
  const margin = { top: 10, right: 10, bottom: 10, left: 10 };
  const diagonal = d3
    .linkHorizontal()
    .x((d) => d.y)
    .y((d) => d.x);

  const tree = d3.tree().nodeSize([dx, dy]);

  function graph(
    data,
    svg,
    {
      label = (d) => d.data.name,
      highlight = () => false,
      marginLeft = 40,
    } = {}
  ) {
    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;

    svg.attr("width", "100%").attr("height", "calc(100% - 15px)");

    const root = d3.hierarchy(data);

    root.x = 0;
    root.y = 0;
    root.id = "wef-0";

    let x0 = Infinity;
    let x1 = -x0;
    root.each((d) => {
      if (d.x > x1) x1 = d.x;
      if (d.x < x0) x0 = d.x;
    });

    root.descendants().forEach((d, i) => {
      if (!d.id || d.id == "00000000-0000-0000-0000-000000000000") {
        d.id = `wef-${i + 1}`;
      }
      d._children = d.children;
      if (d.depth >= openLevel && openLevel != -1) {
        d.children = null;
        d.collapsed = true;
      }
      if (d.data[hasChildrenKey]) d.collapsed = true;
      //if (d.depth && d.data.name.length !== 7) d.children = null;
    });

    const gSvg = svg
      .append("g")
      .attr("viewBox", [0, 0, width, x1 - x0 + dx * 2])
      .style("font", "14px ey_interstate_light")
      .style("user-select", "none");

    const handleZoom = (e) => gSvg.attr("transform", e.transform);

    const zoom = d3.zoom().scaleExtent([0.5, 4]).on("zoom", handleZoom);

    svg.call(zoom);

    const gLink = gSvg
      .append("g")
      .attr("fill", "none")
      .attr("stroke", "#555")
      .attr("stroke-opacity", 0.4)
      .attr("stroke-width", 1.5);

    const gNode = gSvg
      .append("g")
      .attr("cursor", "pointer")
      .attr("pointer-events", "all")
      .attr("font-weight", 400);

    const tooltip = d3
      .select("body")
      .append("div")
      .attr("class", "tooltip")
      .style("position", "absolute")
      .style("pointer-events", "none")
      .style("padding", "10px")
      .style("width", "300px")
      .style("opacity", 0)
      .style("font-family", "ey_interstate_light")
      .style("font-size", "12px")
      .style("background-color", "#fff")
      .style("box-shadow", "2px 2px 5px 2px #C1C1C1")
      .style("border-radius", "5px");

    tooltip
      .append("div")
      .attr("class", "title")
      .style("margin-bottom", "10px")
      .style("font-family", "ey_interstate_bold");

    tooltip
      .append("div")
      .attr("class", "description")
      .style("margin-bottom", "10px");

    const centerNode = (d) =>
      svg.transition().duration(500).call(zoom.translateTo, d.y0, d.x0);

    treeFunctions.centerNode = (d) =>
      svg
        .transition()
        .duration(500)
        .call(
          zoom.transform,
          d3.zoomIdentity
            .scale(1)
            .translate(d.y0 + width / 6, d.x0 + height / 2)
        );

     
    window.treeFunctions = treeFunctions;
    // function centerNode(source) {
    //   t = d3.zoomTransform(baseSvg.node());
    //   x = -source.y0;
    //   y = -source.x0;
    //   x = x * t.k + viewerWidth / 2;
    //   y = y * t.k + viewerHeight / 2;
    //   d3.select('svg').transition().duration(duration)
    //                                .call( zoomListener.transform, d3.zoomIdentity.translate(x,y).scale(t.k) );

    function getCircleColorFill(d) {
      if (d.data[hasChildrenKey]) {
        if (d.data[childrenCountKey] == 0) return theme.palette.grey300.main;
        return theme.palette.blue200.main;
      }
      if (d._children) {
        return theme.palette.blue200.main;
      }
      return theme.palette.grey300.main;
    }

    function getCircleColorStroke(d) {
      if (d.data[hasChildrenKey]) {
        if (d.data[childrenCountKey] == 0) return theme.palette.grey400.main;
        return theme.palette.blue400.main;
      }
      if (d._children) {
        return theme.palette.blue400.main;
      }
      return theme.palette.grey400.main;
    }

    function getChildBarLength(d) {
      if (d.data.level == childLevelName) {
        if (
          d.data[childLevelCountKey] > 0 &&
          root.data[maxChildLevelCountKey] > 0
        ) {
          return (
            (d.data[childLevelCountKey] / root.data[maxChildLevelCountKey]) *
            barLength
          );
        }
        return 0;
      }
      if (d.data[hasChildrenKey] && d.collapsed) {
        return (
          (d.data[childrenCountKey] / root.data[totalChildrenKey]) * barLength
        );
      }
      return 0;
    }

    function getTextAnchorOffset(d) {
      let anchor = getTextAnchor(d);
      if (anchor == "end") {
        return -TEXT_MARGIN;
      }
      return getChildBarLength(d) > 0
        ? getChildBarLength(d) + TEXT_MARGIN * 1.2
        : TEXT_MARGIN;
    }

    function circleNode(dataNode) {
      dataNode
        .append("circle")
        .attr("class", "parent")
        .attr("r", CIRCLE_RADIUS)
        .attr("stroke-width", CIRCLE_STROKE_WIDTH)
        .attr("fill", "white")
        .clone(true)
        .attr("fill", getCircleColorFill)
        .attr("stroke", getCircleColorStroke);
    }

    function childNode(dataNode) {
      dataNode
        .append("circle")
        .attr("r", CIRCLE_RADIUS)
        .attr("stroke", "#555")
        .attr("stroke-opacity", 0.4)
        .attr("stroke-width", CIRCLE_STROKE_WIDTH)
        .attr("fill", "white")
        .clone(true)
        //.attr("fill", (d) => `url(${trendIcon(d.data.trend)})`);
        .attr("fill", (d) => `url(${getChildLevelIcon(d.data)})`);
    }

    function getTextAnchor(d) {
      if (d.data.level == childLevelName) return "start";
      if (d.collapsed) return "start";
      if (!d._children) return "start";
      return "end";
    }

    function textNode(dataNode) {
      dataNode
        .append("text")
        .attr("dy", "0.31em")
        .attr("x", getTextAnchorOffset)
        .attr("text-anchor", getTextAnchor)
        .text((d) => d.data.name)
        .attr("fill", (d) => getNodeColor(d))
        .clone(true)
        .lower()
        .attr("stroke-linejoin", "round")
        .attr("stroke-width", 3)
        .attr("stroke", "white");
    }

    function rectNode(dataNode) {
      dataNode
        .append("rect")
        .attr("width", getChildBarLength)
        .attr("height", 10)
        .attr("x", TEXT_MARGIN)
        .attr("y", -5)
        .attr("fill", theme.palette.blue400.main)
        .attr("stroke", "none")
        .attr("rx", 4)
        .attr("ry", 4);
    }

    function shouldHighlight(d, nodeData) {
      let parents = nodeData.ancestors();

      const linkTargetId = d.target.id;
      const mustHighLight = _.find(parents, (p) => p.id == linkTargetId);

      return mustHighLight;
    }

    function shouldHighlightNode(d, nodeData) {
      let parents = nodeData.ancestors();

      const linkTargetId = d.id;
      const mustHighLight = _.find(parents, (p) => p.id == linkTargetId);

      return mustHighLight;
    }

    function getNodeColor(d, nodeData) {
      return d.data.disabled
        ? theme.palette.grey300.main
        : nodeData && shouldHighlightNode(d, nodeData)
          ? theme.palette.blue500.main
          : theme.palette.grey600.main;
    }

    function nodeMouseOver(e, data, link, nodes) {
      link.attr("stroke-opacity", (d) => shouldHighlight(d, data) && 1);
      link.attr("stroke-width", (d) => shouldHighlight(d, data) && "3px");
      link.attr(
        "stroke",
        (d) => shouldHighlight(d, data) && theme.palette.blue500.main
      );
      nodes.attr("fill", (d) => getNodeColor(d, data));
      nodes.attr("cursor", (d) => (d.data.disabled ? "default" : "pointer"));
      nodes.attr("font-weight", (d) => shouldHighlightNode(d, data) && 600);

      if (!data.data.definition || data.data.disabled) return;

      tooltip
        .style("opacity", 1)
        .style("left", `${e.pageX + 10}px`)
        .style("top", `${e.pageY + 10}px`);
      tooltip.select(".title").html(data.data.name);
      tooltip.select(".description").html(data.data.definition);
      //}
    }

    function nodeMouseOut(e, data, link, nodes) {
      tooltip.style("opacity", 0);
      link
        .attr("stroke-opacity", 0.4)
        .attr("stroke-width", 1.5)
        .attr("stroke", "#555");
      nodes.attr("fill", (d) =>
        d.data.disabled
          ? theme.palette.grey300.main
          : theme.palette.grey600.main
      );
      nodes.attr("font-weight", 400);
    }

    const onNodeClick = async (event, d) => {
      if (d.children) {
        d.descendants().forEach((node) => {
          node._children = _.cloneDeep(node.children);
          node.children = null;
          node.collapsed = true;
        });
        update(d);
        centerNode(d);
      } else {
        if (d.data[hasChildrenKey] && d.collapsed) {
          getDynamicChildren(d.data).then((response) => {
            let { data } = response;
            if (onDataLoaded) {
              data = onDataLoaded(data);
            }
            d.children = [];
            data.forEach((item, i) => {
              let node = d3.hierarchy(item);
              node.id = item.id;
              node.parent = d;
              node.depth = d.depth + 1;
              d.children.push(node);
            });
            d._children = _.cloneDeep(d.children);
            d.collapsed = false;
            update(d);
            centerNode(d);
          });
        } else if (d.data.level == childLevelName) {
          if (!d.data.disabled) {
            setDrawerState({
              open: true,
              id: d.data.id,
            });
          }
        } else {
          d.children = _.cloneDeep(d._children);
          d.collapsed = false;

          update(d);
          centerNode(d);
        }
      }
    };

    function update(source) {
      tree(root);

      const duration = 250;
      const nodes = root.descendants().reverse();
      const links = root.links();

      const getNode = (id) => nodes.find((a) => a.id == id);

      const transition = svg
        .transition()
        .duration(duration)
        .attr("viewBox", [
          -margin.left,
          -margin.top,
          width + margin.right,
          height + margin.bottom,
        ])
        .tween(
          "resize",
          d3.window.ResizeObserver ? null : () => () => svg.dispatch("toggle")
        );

      // Update the nodes...
      const node = gNode.selectAll("g.node").data(nodes, (d) => d.id);

      // Enter any new nodes at the parent's previous position.
      const dataNode = node
        .enter()
        .append("g")
        .attr("id", (d) => d.id)
        .attr("class", "node")
        .attr(
          "transform",
          (d) => `translate(${source.y0 || 0},${source.x0 || 0})`
        )
        .attr("fill-opacity", 0)
        .attr("stroke-opacity", 0)
        .on("click", onNodeClick)
        .on("mouseover", (e, d) =>
          nodeMouseOver(e, d, gLink.selectAll("path"), gNode.selectAll("text"))
        )
        .on("mouseout", (e, d) =>
          nodeMouseOut(e, d, gLink.selectAll("path"), gNode.selectAll("text"))
        );

      circleNode(dataNode.filter((a) => a.data.level != childLevelName));
      childNode(dataNode.filter((a) => a.data.level == childLevelName));
      textNode(dataNode);

      rectNode(
        dataNode.filter(
          (a) =>
            (a.data[hasChildrenKey] && a.data[childrenCountKey] > 0) ||
            a.data.level == childLevelName
        )
      );

      // Transition nodes to their new position.
      node
        .merge(dataNode)
        .transition(transition)
        .attr("transform", (d) => `translate(${d.y},${d.x})`)
        .attr("fill-opacity", 1)
        .attr("stroke-opacity", 1);

      // Transition exiting nodes to the parent's new position.
      node
        .exit()
        .transition(transition)
        .remove()
        .attr("transform", (d) => `translate(${source.y},${source.x})`)
        .attr("fill-opacity", 0)
        .attr("stroke-opacity", 0);

      gLink
        .selectAll("path")
        .data(links, (d) => d.id)
        .join(
          (enter) =>
            enter
              .append("path")
              .transition(transition)
              .attr("d", (d) =>
                diagonal({
                  source: getNode(d.source.id),
                  target: getNode(d.target.id),
                })
              ),
          (update) =>
            update.transition(transition).attr("d", (d) =>
              diagonal({
                source: getNode(d.source.id),
                target: getNode(d.target.id),
              })
            ),
          (exit) => exit.remove()
        );

      // Stash the old positions for transition.
      root.eachBefore((d) => {
        d.x0 = d.x;
        d.y0 = d.y;
      });

      gSvg
        .selectAll("g.node")
        .filter((d) => d.id == source.id)
        .selectAll("text")
        .attr("text-anchor", getTextAnchor)
        .attr("x", getTextAnchorOffset);
      gSvg
        .selectAll("g.node")
        .filter((d) => d.id == source.id)
        .selectAll("rect")
        .attr("opacity", (d) => (d.collapsed ? 1 : 0));
    }

    update(root);

    svg.transition().call(zoom.translateTo, root.y0 + width / 3, root.x0);

    return svg.node();
  }

  const Pattern = ({ id, image }) => (
    <pattern id={id} x="0" y="0" height="1" width="1">
      <image
        height={CIRCLE_RADIUS * 2}
        width={CIRCLE_RADIUS * 2}
        xlinkHref={image}
      />
    </pattern>
  );

  const ref = useD3((svg) => {
    graph(data, svg);
  }, []);

  return (
    <>
      <StyledContainer ref={containerRef}>
        <svg
          ref={ref}
          style={{
            userSelect: "none",
            border: `1px ${theme.palette.grey200.main} solid`,
            cursor: "move",
          }}
        >
          <defs>
            {icons.map((item, i) => (
              <Pattern key={`pattern-${i}`} id={item.id} image={item.image} />
            ))}
          </defs>
        </svg>
        <SFIconButtonStyled
          type="button"
          onClick={() => treeFunctions.centerNode({ x0: 0, y0: 0 })}
        >
          <CenterFocusWeakIcon color="grey500" />
        </SFIconButtonStyled>
      </StyledContainer>
      {drawer.open &&
        childDetail({
          id: drawer.id,
          open: drawer.open,
          onClose: () => setDrawerState({ open: false }),
        })}
    </>
  );
};

export default SFTree;
